import {
  EXEMPTION_STATUSES,
  EXEMPTION_STATUS_LABELS,
  FRICTION_STATUSES,
  FRICTION_STATUS_LABELS,
  LEAD_STATUS_LABELS,
  TEST_RESULTS,
  TEST_RESULT_LABELS,
  type ExemptionStatus,
  type FrictionStatus,
  type TestResult,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  Input,
  LoadingState,
  StatPill,
  cn,
  toast,
  type DataTableColumn,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, FlaskConical, Paperclip, Search, Square, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  bulkMarkFrictionClean,
  fetchBuildingLeadGrid,
  type BuildingLeadFilters,
  type BuildingLeadGridRow,
} from '../../../api/lead.js';
import { ApiError } from '../../../lib/api-client.js';
import { LEAD_STATUS_TONE } from '../units/lead-status.js';

interface Props {
  buildingId: string;
  /** When true, the current user can run bulk actions (admin or
   *  building_manager). The bulk-action bar still renders for
   *  read-only users but the action button is hidden. */
  canBulkAct: boolean;
}

/** All filters live in component state — the URL isn't kept in sync
 *  because the page is a tab inside the building detail page and
 *  back/forward navigation should restore the building, not the
 *  filter set. */
const DEFAULT_FILTERS: BuildingLeadFilters = {};

export function BuildingLeadTab({ buildingId, canBulkAct }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<BuildingLeadFilters>(DEFAULT_FILTERS);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const debounced = useDebounced(query, 200);
  const effectiveFilters: BuildingLeadFilters = {
    ...filters,
    q: debounced.trim() || undefined,
  };

  const gridQuery = useQuery({
    queryKey: ['building-lead', buildingId, effectiveFilters],
    queryFn: () => fetchBuildingLeadGrid(buildingId, effectiveFilters),
  });

  const bulkFrictionMutation = useMutation({
    mutationFn: () =>
      bulkMarkFrictionClean(buildingId, {
        unit_ids: Array.from(selected),
        change_note: 'Bulk marked clean via building grid',
      }),
    onSuccess: (result) => {
      toast.success(
        `Marked ${result.updated} unit${result.updated === 1 ? '' : 's'} friction-clean` +
          (result.skipped.length > 0 ? ` (${result.skipped.length} already clean)` : ''),
      );
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['building-lead', buildingId] });
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'units'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-rollup'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Bulk action failed');
    },
  });

  function toggleSelected(unitId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  }

  function toggleAll(rows: BuildingLeadGridRow[]) {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.unitId));
    });
  }

  const data = gridQuery.data;
  // `useMemo` guards the array identity so the `columns` memo below
  // doesn't get a new `rows` reference on every render — which would
  // re-create the column cells whenever any sibling state changed.
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const counts = data?.counts;

  const columns: DataTableColumn<BuildingLeadGridRow>[] = useMemo(
    () => [
      {
        id: 'select',
        description: 'Select units to update their lead records together.',
        header: (
          <button
            type="button"
            aria-label="Select all"
            onClick={() => toggleAll(rows)}
            className="flex items-center justify-center text-fg-muted hover:text-fg"
          >
            {selected.size === rows.length && rows.length > 0 ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
          </button>
        ),
        className: 'w-10',
        cell: (row) => (
          <button
            type="button"
            aria-label={selected.has(row.unitId) ? 'Deselect' : 'Select'}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelected(row.unitId);
            }}
            className="flex items-center justify-center text-fg-muted hover:text-fg"
          >
            {selected.has(row.unitId) ? (
              <CheckSquare className="h-4 w-4 text-primary" />
            ) : (
              <Square className="h-4 w-4" />
            )}
          </button>
        ),
      },
      {
        id: 'unit',
        header: 'Unit',
        description: 'The unit this lead row belongs to.',
        className: 'w-24 font-medium',
        cell: (row) => row.unitNumber,
      },
      {
        id: 'friction',
        header: 'Friction',
        description: 'Whether access or scheduling problems are blocking lead work. Clean means none are open.',
        className: 'w-28',
        cell: (row) =>
          row.frictionStatus === 'clean' ? (
            <span className="rounded-sm bg-success-subtle px-1.5 py-0.5 text-xs font-medium text-success">
              Clean
            </span>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: 'exemption',
        header: 'Exemption',
        description: 'Lead-paint exemption status, and whether an exemption letter is attached.',
        className: 'w-44',
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-secondary">
            <span className="capitalize">{EXEMPTION_STATUS_LABELS[row.exemptionStatus]}</span>
            {row.exemptionStatus === 'approved' && row.exemptionLetterDocumentId ? (
              <Paperclip className="h-3 w-3 text-fg-muted" aria-label="Letter attached" />
            ) : null}
          </span>
        ),
      },
      {
        id: 'test',
        header: 'Test result',
        description: 'Latest lead test outcome. A positive result includes how many samples failed.',
        className: 'w-32',
        cell: (row) => {
          if (row.testResult === 'positive') {
            return (
              <span className="text-xs font-medium text-danger">
                Positive ({row.positiveCount ?? 0})
              </span>
            );
          }
          if (row.testResult === 'negative') {
            return <span className="text-xs font-medium text-success">Negative</span>;
          }
          return <span className="text-fg-muted">—</span>;
        },
      },
      {
        id: 'xrf',
        header: 'XRF date',
        description: 'When the most recent X-ray fluorescence lead test was done.',
        className: 'w-28 text-fg-secondary text-xs',
        cell: (row) =>
          row.latestXrfDate ? (
            <span title={row.latestXrfDate}>{relativeDate(row.latestXrfDate)}</span>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        description: 'Overall lead status for the unit.',
        className: 'w-44',
        cell: (row) => (
          <StatPill
            tone={LEAD_STATUS_TONE[row.leadStatus]}
            value={LEAD_STATUS_LABELS[row.leadStatus]}
            withDot
          />
        ),
      },
    ],
     
    [rows, selected],
  );

  if (gridQuery.isLoading) return <LoadingState rows={6} />;

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-fg-muted" aria-hidden />
                <h3 className="text-sm font-semibold">Lead tracking</h3>
              </div>
              {counts ? (
                <p className="mt-1 text-xs text-fg-muted">
                  {counts.total} unit{counts.total === 1 ? '' : 's'} · {counts.untested} untested ·{' '}
                  <span className={counts.testedPositive > 0 ? 'text-danger font-medium' : ''}>
                    {counts.testedPositive} positive
                  </span>{' '}
                  · {counts.exemptApproved} exempt approved · {counts.frictionCleanOnly} friction
                  clean only
                </p>
              ) : null}
            </div>
          </div>

          <FilterRow filters={filters} onChange={setFilters} query={query} onQueryChange={setQuery} />

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.unitId}
            empty={
              counts && counts.total === 0
                ? 'No units in this building yet. Add units first.'
                : 'No units match the current filters.'
            }
            onRowClick={(row) => navigate(`/units/${row.unitId}`)}
          />
        </CardContent>
      </Card>

      {selected.size > 0 ? (
        <div
          className={cn(
            'pointer-events-auto fixed inset-x-0 bottom-4 z-40 mx-auto flex max-w-2xl items-center justify-between gap-3',
            'rounded-lg border border-border bg-surface-elevated px-4 py-2 shadow-lg',
          )}
          role="region"
          aria-label="Bulk actions"
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{selected.size} selected</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              aria-label="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {canBulkAct ? (
            <Button
              size="sm"
              onClick={() => bulkFrictionMutation.mutate()}
              disabled={bulkFrictionMutation.isPending}
            >
              {bulkFrictionMutation.isPending
                ? 'Updating…'
                : `Mark ${selected.size} friction clean`}
            </Button>
          ) : (
            <span className="text-xs text-fg-muted">
              Bulk actions require admin or building-manager role.
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

interface FilterRowProps {
  filters: BuildingLeadFilters;
  onChange: (next: BuildingLeadFilters) => void;
  query: string;
  onQueryChange: (next: string) => void;
}

function FilterRow({ filters, onChange, query, onQueryChange }: FilterRowProps) {
  function toggleArray<T extends string>(field: keyof BuildingLeadFilters, value: T) {
    const current = (filters[field] as T[] | undefined) ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [field]: next.length === 0 ? undefined : next });
  }

  function setQuick(field: 'has_positives' | 'untested' | 'friction_clean', enabled: boolean) {
    onChange({ ...filters, [field]: enabled ? true : undefined });
  }

  function clearAll() {
    onChange(DEFAULT_FILTERS);
    onQueryChange('');
  }

  const anyFilterActive =
    !!filters.friction_status ||
    !!filters.exemption_status ||
    !!filters.test_result ||
    filters.has_positives === true ||
    filters.untested === true ||
    filters.friction_clean === true ||
    query.trim().length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ChipGroup label="Friction">
        {FRICTION_STATUSES.map((v) => (
          <Chip
            key={v}
            active={filters.friction_status?.includes(v) ?? false}
            onClick={() => toggleArray<FrictionStatus>('friction_status', v)}
          >
            {FRICTION_STATUS_LABELS[v]}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Exemption">
        {EXEMPTION_STATUSES.map((v) => (
          <Chip
            key={v}
            active={filters.exemption_status?.includes(v) ?? false}
            onClick={() => toggleArray<ExemptionStatus>('exemption_status', v)}
          >
            {EXEMPTION_STATUS_LABELS[v]}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Test">
        {TEST_RESULTS.map((v) => (
          <Chip
            key={v}
            active={filters.test_result?.includes(v) ?? false}
            onClick={() => toggleArray<TestResult>('test_result', v)}
          >
            {TEST_RESULT_LABELS[v]}
          </Chip>
        ))}
      </ChipGroup>
      <ChipGroup label="Quick">
        <Chip
          active={filters.has_positives === true}
          onClick={() => setQuick('has_positives', filters.has_positives !== true)}
        >
          Has positives
        </Chip>
        <Chip
          active={filters.untested === true}
          onClick={() => setQuick('untested', filters.untested !== true)}
        >
          Untested
        </Chip>
        <Chip
          active={filters.friction_clean === true}
          onClick={() => setQuick('friction_clean', filters.friction_clean !== true)}
        >
          Friction clean
        </Chip>
      </ChipGroup>
      <div className="ml-auto flex items-center gap-1.5">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-muted"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Unit number"
            className="h-8 w-44 pl-7 text-xs"
          />
        </div>
        {anyFilterActive ? (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ChipGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
        {label}
      </span>
      <div className="inline-flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-6 items-center rounded-full px-2 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        active
          ? 'bg-primary text-primary-fg'
          : 'border border-border bg-surface text-fg-secondary hover:bg-surface-muted',
      )}
    >
      {children}
    </button>
  );
}

// Tiny debounce hook — keeps the API call cadence sane while the
// user types into the search input.
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
